<template>
    <div>
        <AppBreadCrumb :paths="[{ title: $t('message.announcement') }]" :select="true">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('message.search')"
                class="search-input"
                @search="onSearch"
            />
            <a-button class="actionButtonBreadcrumb" @click="showModalWithoutEdit">
                <md-icon>add</md-icon>
                {{ $t("message.newAnnouncement") }}
            </a-button>
        </AppBreadCrumb>

        <b-container fluid>
            <b-row>
                <b-col>
                    <a-table
                        :key="'vtable-' + renderCount"
                        :columns="cols()"
                        :dataSource="getFilteredRows()"
                        :loading="isBusy"
                        :locale="{ emptyText: $t('message.noNewAnnouncement') }"
                        :pagination="false"
                        class="custom-table2"
                        rowKey="_id"
                        @change="tableChange"
                    >
                        <div :slot="'contentSlot'" slot-scope="data, item">{{ domDecoder(data) }}</div>

                        <div :key="'actionsSlot-' + $i18n.locale" :slot="'actionsSlot'" slot-scope="data, item">
                            <div class="d-flex w-100 justify-content-center">
                                <a-button class="actionButton d-flex align-items-center" @click="edit(item._id)">
                                    <span style="margin-right: 16px; display: block">{{ $t("message.edit") }}</span>
                                    <ActionEdit />
                                </a-button>
                                <a-button class="actionButton d-flex align-items-center" @click="remove(item._id)">
                                    <span style="margin-right: 16px; display: block">{{
                                        $t("message.deleteField")
                                    }}</span>
                                    <ActionDelete />
                                </a-button>
                            </div>
                        </div>
                    </a-table>
                    <div class="d-flex justify-content-end">
                        <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="announcements.total" />
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <Modal2
            v-if="isModalShow"
            id="announcement-form"
            :title="editMode ? $t('message.updateAnnouncementForm') : $t('message.newAnnouncementForm')"
            :show-footer="false"
            @cancel="hideModal"
        >
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                <a-alert
                    v-if="$validator.errors.items.length && submitClicked"
                    :message="$t('message.error')"
                    class="mb-2"
                    showIcon
                    type="error"
                >
                    <div slot="description">
                        <div v-for="(err, index) in $validator.errors.items" :key="'err-' + index">
                            <span>
                                {{ err.msg }}
                            </span>
                        </div>
                    </div>
                </a-alert>
                <b-row>
                    <b-col md="12">
                        <b-row>
                            <b-col md="12">
                                <MultiContent ref="MultiContent" v-model="multiContentComp" />
                            </b-col>
                            <b-col class="mt-3" md="12">
                                <b-form-group label="Durum">
                                    <b-form-radio-group v-model="announcement.status" name="status" required>
                                        <b-form-radio :value="true">{{ $t("message.active") }}</b-form-radio>
                                        <b-form-radio :value="false">{{ $t("message.passive") }}</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                            </b-col>
                            <b-col md="12">
                                <b-button :disabled="loading" type="submit" variant="primary">
                                    <template v-if="!loading">
                                        <span v-if="!editMode">
                                            {{ $t("message.save") }}
                                        </span>
                                        <span v-else>
                                            {{ $t("message.update") }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <font-awesome-icon icon="spinner" spin />
                                    </template>
                                </b-button>
                                <b-button @click="hideModal">
                                    <template>
                                        {{ $t("message.cancel") }}
                                    </template>
                                </b-button>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-form>
        </Modal2>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faTimes, faPlusCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../components/common/AppBreadCrumb.vue";
import { log2 } from "../../utils/log2";
import * as _ from "lodash";
import { tableSorter } from "../../utils/utils";
import Pagination from "../../components/Pagination.vue";
import ActionEdit from "../../components/svg/ActionEdit.vue";
import ActionDelete from "../../components/svg/ActionDelete.vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue";
import MultiContent from "../../components/MultiContent.vue";
import { sendSocketMessage } from "../../utils/socketStream";
import Modal2 from "../../components/common/Modal2.vue";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);

const announcementInitialObject = {
    _id: null,
    languageContent: {},
    status: true,
};
const log = log2("Announcements");
export default {
    name: "announcements",
    components: { MultiContent, AppBreadCrumb, Pagination, ActionEdit, ActionDelete, CKEditor, Modal2 },
    data() {
        return {
            isModalShow: false,
            isBusy: true,
            loading: false,
            editMode: false,
            pageSizes: ["10", "25", "50", "100"],
            searchText: undefined,
            announcement: _.cloneDeep(announcementInitialObject),
            editor: ClassicEditor,
            submitClicked: false,
            selectedWMType: null,
            editorConfig: () => ({
                // The configuration of the editor.
                removePlugins: ["MediaEmbed", "AutoMediaEmbed"],
            }),
            cols: () => [
                {
                    dataIndex: "languageContent." + this.$i18n.locale + ".title_",
                    title: this.$t("message.announcementTitle"),
                    ...tableSorter("languageContent." + this.$i18n.locale + ".title_", "string", this.currentSort),
                },
                {
                    dataIndex: "languageContent." + this.$i18n.locale + ".description_",
                    title: this.$t("message.description"),
                    ...tableSorter(
                        "languageContent." + this.$i18n.locale + ".description_",
                        "string",
                        this.currentSort
                    ),
                    scopedSlots: { customRender: "contentSlot" },
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: "10%",
                },
            ],
            perPage: 100,
            page: 1,
            renderCount: 0,
            currentSort: {
                columnKey: "enable",
                order: "ascend",
            },
        };
    },
    computed: {
        ...mapState({
            announcements: (state) => state.announcement.announcements,
            languages: (state) => state.language.languages,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            systemLanguages: (state) => _.get(state.setting, "settings.systemLanguages", []),
            site: (state) => state.site.site,
        }),
        multiContentComp: {
            get() {
                return [
                    {
                        type: "input",
                        field: "title_",
                        label: this.$t("message.announcement"),
                        placeholder: this.$t("message.announcementTitle"),
                        data: (this.systemLanguages || []).reduce((acc, lang) => {
                            acc[lang] = _.get(this.announcement.languageContent, `[${lang}].title_`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                    {
                        type: "rich",
                        field: "description_",
                        label: this.$t("message.description"),
                        placeholder: this.$t("message.infoAboutAnnouncement"),
                        data: (this.systemLanguages || []).reduce((acc, lang) => {
                            acc[lang] = _.get(this.announcement.languageContent, `[${lang}].description_`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.announcement.languageContent = (this.systemLanguages || []).reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
    },
    methods: {
        ...mapActions({
            getAnnouncement: "announcement/getAnnouncement",
            getAnnouncements: "announcement/getAnnouncements",
            createAnnouncement: "announcement/createAnnouncement",
            updateAnnouncement: "announcement/updateAnnouncement",
            deleteAnnouncement: "announcement/deleteAnnouncement",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.isModalShow = true;
            this.editMode = false;
        },

        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },
        domDecoder(str) {
            let parser = new DOMParser();
            let dom = parser.parseFromString("<!doctype html><body>" + str, "text/html");
            return dom.body.textContent;
        },
        getFilteredRows() {
            return this.searchText
                ? this.announcements.data.filter((x) =>
                      x.languageContent[this.$i18n.locale].title_
                          .toLocaleLowerCase("tr-TR")
                          .includes(this.searchText.toLocaleLowerCase("tr-TR"))
                  )
                : this.announcements.data;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
        },
        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.page = current;
            this.perPage = pageSize;
        },
        get() {
            this.isBusy = true;
            this.getAnnouncements({ perPage: this.perPage, page: this.page }).then(() => {
                this.isBusy = false;
            });
        },
        edit(id) {
            this.getAnnouncement(id).then((response) => {
                this.editMode = true;
                this.announcement = response;
                /*this.$bvModal.show("announcement-form");*/
                this.showModal();
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.douYouWishToDeleteAnnouncement"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteAnnouncement(id);
                    this.$toasted.show(this.$t("message.announcementDeleted"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                });
        },
        reset() {
            this.announcement = {
                ...announcementInitialObject,
            };
            this.submitClicked = false;
            this.selectedWMType = null;
        },
        toggleChange(announcement) {
            return (newState) => {
                announcement.enable = newState;
                this.updateAnnouncement(announcement);
            };
        },
        handleSave() {
            this.submitClicked = true;
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.$refs.MultiContent.validate().then((isMultiContentValid) => {
                        if (isMultiContentValid) {
                            this.loading = true;
                            const isCreate = !this.announcement._id;

                            const result = isCreate
                                ? this.createAnnouncement(this.announcement)
                                : this.updateAnnouncement(this.announcement);

                            result
                                .then((response) => {
                                    this.$toasted.show(this.$t("message.announcementSaved"), {
                                        duration: 3000,
                                        position: "bottom-center",
                                    });

                                    if (isCreate) {
                                        localStorage.setItem("announcementId", "new");
                                        sendSocketMessage("admin new_announcement", {
                                            siteId: this.site._id,
                                        });
                                    }

                                    /*this.$bvModal.hide("announcement-form");*/
                                    this.hideModal();
                                    this.reset();
                                    this.get();
                                })
                                .finally(() => {
                                    this.loading = false;
                                    this.submitClicked = false;
                                });
                        }
                    });
                } else {
                    this.loading = false;
                }
            });
        },
    },
    mounted() {
        this.get();
    },
    watch: {
        page(newValue, oldValue) {
            if (newValue) {
                this.get();
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.get();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
#announcement-form {
    button {
        float: right;
        margin-left: 10px;
    }
}

.tabs {
    background-color: #fff;
    background-clip: border-box;
    border: 1px solid rgba(0, 0, 0, 0.125);
    border-radius: 0.25rem;
}
</style>
<style lang="scss">
#announcement-form {
    .ant-modal {
        width: 670px !important;
    }
}
</style>
